import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Megaphone, Calendar, AlertTriangle, Gift, Info, ChevronLeft, ChevronRight } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxDisplay, setMaxDisplay] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    fetchAnnouncements();
    fetchSettings();
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (announcements.length <= 1 || isPaused) {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
      return;
    }

    autoScrollRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % announcements.length);
    }, 5000); // Change every 5 seconds

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [announcements.length, isPaused]);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      // Filter out dismissed announcements
      const dismissedIds = JSON.parse(localStorage.getItem("dismissedAnnouncements") || "[]");
      const activeAnnouncements = res.data.filter(a => !dismissedIds.includes(a.id));
      setAnnouncements(activeAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/announcements/settings`);
      setMaxDisplay(res.data.max_display || 1);
    } catch (error) {
      // Default to 1 if settings not found
      setMaxDisplay(1);
    }
  };

  const handleDismissAll = () => {
    const allIds = announcements.map(a => a.id);
    const dismissedIds = JSON.parse(localStorage.getItem("dismissedAnnouncements") || "[]");
    localStorage.setItem("dismissedAnnouncements", JSON.stringify([...dismissedIds, ...allIds]));
    setAnnouncements([]);
  };

  const handlePrev = () => {
    setCurrentIndex(prev => prev === 0 ? announcements.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % announcements.length);
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case "event":
        return {
          icon: Calendar,
          bg: "from-purple-500/75 to-indigo-600/75",
          iconBg: "bg-purple-400/30"
        };
      case "promo":
        return {
          icon: Gift,
          bg: "from-emerald-500/75 to-teal-600/75",
          iconBg: "bg-emerald-400/30"
        };
      case "alert":
        return {
          icon: AlertTriangle,
          bg: "from-amber-500/75 to-orange-600/75",
          iconBg: "bg-amber-400/30"
        };
      default:
        return {
          icon: Info,
          bg: "from-cyan-500/75 to-blue-600/75",
          iconBg: "bg-cyan-400/30"
        };
    }
  };

  // Don't render if no announcements
  if (announcements.length === 0) return null;

  // Limit to max display setting
  const displayAnnouncements = announcements.slice(0, maxDisplay);
  
  // Safety check for current index
  const safeIndex = currentIndex < displayAnnouncements.length ? currentIndex : 0;
  const current = displayAnnouncements[safeIndex];
  
  // Double check current exists
  if (!current) return null;
  
  const config = getTypeConfig(current.type);
  const Icon = config.icon;

  return (
    <div 
      className={`bg-gradient-to-r ${config.bg} backdrop-blur-md text-white py-3 sm:py-3 px-3 sm:px-4 relative`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto">
        {/* Mobile Layout - Stack vertically with minimal controls */}
        <div className="flex sm:hidden items-start gap-2">
          {/* Dismiss button - inline at start on mobile */}
          <button
            onClick={handleDismissAll}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0 mt-0.5"
            aria-label="Dismiss all announcements"
            title="Close all announcements"
          >
            <X className="w-4 h-4" />
          </button>
          
          {/* Content takes remaining width on mobile */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base leading-tight">
              {current.title}
            </p>
            <div 
              className="text-sm text-white/90 mt-1 [&_a]:underline [&_a]:font-medium [&_a:hover]:text-white line-clamp-3"
              dangerouslySetInnerHTML={{ __html: current.content }}
            />
            {/* Mobile navigation dots - bottom of content */}
            {displayAnnouncements.length > 1 && (
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handlePrev}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1.5">
                  {displayAnnouncements.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === safeIndex ? "bg-white" : "bg-white/40"
                      }`}
                      aria-label={`Go to announcement ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleNext}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout - Original horizontal layout */}
        <div className="hidden sm:flex items-center gap-4">
          {/* Left side: Controls (dismiss all, navigation) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDismissAll}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Dismiss all announcements"
              title="Close all announcements"
            >
              <X className="w-4 h-4" />
            </button>
            
            {displayAnnouncements.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Previous announcement"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                  {displayAnnouncements.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === safeIndex ? "bg-white" : "bg-white/40"
                      }`}
                      aria-label={`Go to announcement ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleNext}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Next announcement"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          {/* Content */}
          <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
            <div className={`${config.iconBg} p-2 rounded-full flex-shrink-0`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="font-semibold text-sm md:text-base truncate">
                {current.title}
              </p>
              <div 
                className="text-sm text-white/90 [&_a]:underline [&_a]:font-medium [&_a:hover]:text-white max-h-16 overflow-y-auto scrollbar-thin scrollbar-thumb-white/30"
                style={{ 
                  whiteSpace: current.content?.includes('<br') ? 'normal' : 'nowrap',
                  textOverflow: current.content?.includes('<br') ? 'clip' : 'ellipsis',
                  overflow: current.content?.includes('<br') ? 'auto' : 'hidden'
                }}
                dangerouslySetInnerHTML={{ __html: current.content }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
